import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Active } from "@dnd-kit/core";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { logError } from "~/shared/services/error-reporting";
import { toast } from "~/utils/toast";
import { SortableOverlay } from "./sortable-overlay";
import {
  SortableListItem,
  type SortableListItemProps,
} from "./sortable-list-item";

export interface BaseItem {
  id: string;
  value: string;
  description?: string;
  infoText?: string;
  subtitle?: string;
}

interface SortableListProps {
  items: BaseItem[];
  onChangeOrder?: (items: BaseItem[]) => void;
  onRemove?: (item: BaseItem | undefined) => void | Promise<void>;
  onEdit?: (item: BaseItem, value: string) => void;
  disabled?: boolean;
  copyToClipboard?: boolean;
  successToastMessage?: string;
  valueClassName?: SortableListItemProps["valueClassName"];
  descriptionVariant?: SortableListItemProps["descriptionVariant"];
}

export const SortableList: FC<SortableListProps> = ({
  items: baseItems,
  onChangeOrder,
  onRemove,
  onEdit,
  disabled = false,
  copyToClipboard,
  successToastMessage,
  valueClassName,
  descriptionVariant,
}) => {
  const [items, setItems] = useState(baseItems);
  const lastItemRef = useRef<HTMLLIElement | null>(null);
  const previousBaseItemsLengthRef = useRef(baseItems.length);

  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;

    // mirrors the baseItems prop into local state that is also locally reordered during drag; must resync when the prop changes, and the same effect schedules the scroll-to-new-item
    setItems(baseItems);
    if (baseItems.length > previousBaseItemsLengthRef.current) {
      scrollTimeout = setTimeout(() => {
        lastItemRef.current?.scrollIntoView?.({ behavior: "smooth" });
      }, 100);
    }
    previousBaseItemsLengthRef.current = baseItems.length;

    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [baseItems]);

  const [activeElement, setActiveElement] = useState<Active>();
  const activeItem = useMemo(
    () => items.find((item) => item.id === activeElement?.id),
    [activeElement, items],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleRemoveItem = useCallback(
    (id: string) => {
      const item = items.find((currentItem) => currentItem.id === id);

      try {
        Promise.resolve(onRemove?.(item)).catch(logError);
      } catch (error) {
        logError(error);
      }
    },
    [items, onRemove],
  );

  const handleCopyToClipboard = async (value: string) => {
    if (!copyToClipboard) return;

    try {
      await navigator.clipboard.writeText(value);

      if (successToastMessage) toast.success(successToastMessage);
    } catch (error) {
      logError(error);
    }
  };

  const renderItem = (index: number, item: BaseItem, isLast: boolean) => {
    return (
      <li
        ref={isLast ? lastItemRef : null}
        key={item.id}
        className="w-full list-none divide-y divide-secondary-100"
      >
        <SortableListItem
          index={index}
          {...item}
          disabled={disabled}
          valueClassName={valueClassName}
          descriptionVariant={descriptionVariant}
          onRemove={onRemove ? () => handleRemoveItem(item.id) : undefined}
          onChange={onEdit ? (value) => onEdit(item, value) : undefined}
          onCopy={
            copyToClipboard
              ? () => handleCopyToClipboard(item.value)
              : undefined
          }
        />
      </li>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => setActiveElement(active)}
      onDragEnd={({ active, over }) => {
        if (!disabled && onChangeOrder && over && active.id !== over?.id) {
          const activeIndex = items.findIndex(({ id }) => id === active.id);
          const overIndex = items.findIndex(({ id }) => id === over.id);

          const updated = arrayMove(items, activeIndex, overIndex);

          setItems(updated);

          // Wait until dragging animation has finished before saving
          setTimeout(() => onChangeOrder(updated), 100);
        }

        setActiveElement(undefined);
      }}
      onDragCancel={() => setActiveElement(undefined)}
    >
      <SortableContext items={items}>
        <ul
          className="flex w-full flex-col m-0 p-0 list-none items-start justify-start"
          data-testid="sortable-list"
        >
          {items.map((item, index) =>
            renderItem(index, item, index === items.length - 1),
          )}
        </ul>
      </SortableContext>

      <SortableOverlay>
        {activeItem && renderItem(-1, activeItem, false)}
      </SortableOverlay>
    </DndContext>
  );
};
